(() => {
  'use strict';

  const SOURCE_SELECTOR = '[data-gb-po-controller]';
  const GB_PURCHASE_OPTIONS_VERSION = '14.0.0';
  const PRODUCT_FORM_SELECTOR =
    'form[action*="/cart/add"]:has([name="id"]):has(.product-form--atc-button)';
  const AGO_PATTERN =
    /^\s*\d+\s*(?:seconds?|minutes?|hours?|days?|weeks?|months?|years?)\s+ago\s*$/i;
  const MAX_ADDON_QUANTITY = 99;
  const VALID_SHIPPING_TYPES = new Set([
    'Parcel',
    'Freight',
    'Parcel_Freight'
  ]);

  function normalizeShippingType(value) {
    const shippingType = String(value || '').trim();

    return VALID_SHIPPING_TYPES.has(shippingType)
      ? shippingType
      : '';
  }

  function updateItemShippingData(item, option = null) {
    if (!item) {
      return;
    }

    const productShippingType = normalizeShippingType(
      item.dataset.productShippingType
    );

    const rawVariantShippingType =
      option
        ? option.dataset.shippingType
        : item.dataset.variantShippingType;

    const variantShippingType = normalizeShippingType(
      rawVariantShippingType
    );

    item.dataset.productShippingType = productShippingType;
    item.dataset.variantShippingType = variantShippingType;
    item.dataset.resolvedShippingType =
      variantShippingType || productShippingType;
  }

  function notifyDeliverySystem(module) {
    if (!module) {
      return;
    }

    module.dispatchEvent(
      new CustomEvent('gb:purchase-options-change', {
        bubbles: true,
        detail: {
          instanceId: module.dataset.gbPoInstance || ''
        }
      })
    );
  }

  function normalizeText(value) {
    return String(value || '').trim().replace(/\s+/g, ' ');
  }

  function makeFormatter(currency) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: currency || 'USD',
      currencyDisplay: 'narrowSymbol'
    });
  }

  function clampInteger(value, min = 0, max = MAX_ADDON_QUANTITY) {
    const parsed = Number.parseInt(value, 10);

    if (!Number.isFinite(parsed)) {
      return min;
    }

    return Math.min(max, Math.max(min, parsed));
  }

  function findProductForm() {
    const exact = document.querySelector(PRODUCT_FORM_SELECTOR);

    if (exact) {
      return exact;
    }

    return (
      Array.from(document.querySelectorAll('form[action*="/cart/add"]')).find(
        (form) =>
          form.querySelector('[name="id"]') &&
          form.querySelector(
            '.product-form--atc-button, button[type="submit"], input[type="submit"]'
          )
      ) || null
    );
  }

  function findSubmitButton(form) {
    return (
      form.querySelector('.product-form--atc-button') ||
      form.querySelector('button[type="submit"], input[type="submit"]')
    );
  }

  function getButtonTextTarget(button) {
    if (!button || button.tagName === 'INPUT') {
      return button;
    }

    return (
      button.querySelector(
        '.atc-button--text, [data-button-text], .button__text, .button-text'
      ) || button
    );
  }

  function getButtonText(button) {
    if (!button) {
      return '';
    }

    if (button.tagName === 'INPUT') {
      return button.value || '';
    }

    return normalizeText(getButtonTextTarget(button).textContent);
  }

  function setButtonText(button, text) {
    if (!button) {
      return;
    }

    if (button.tagName === 'INPUT') {
      button.value = text;
      return;
    }

    getButtonTextTarget(button).textContent = text;
  }

  function getBaseQuantity(form) {
    const input = form.querySelector('[name="quantity"]');
    const quantity = Number(input?.value || 1);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function getItemQuantity(item) {
    const input = item?.querySelector('[data-gb-po-qty-input]');

    if (!input || input.disabled) {
      return 0;
    }

    return clampInteger(input.value);
  }

  function setItemQuantity(item, quantity) {
    const input = item?.querySelector('[data-gb-po-qty-input]');

    if (!input || input.disabled) {
      return 0;
    }

    const normalized = clampInteger(quantity);
    input.value = String(normalized);
    item.classList.toggle('is-selected', normalized > 0);

    return normalized;
  }

  function setItemAvailability(item, available) {
    if (!item) {
      return;
    }

    item.dataset.available = available ? 'true' : 'false';
    item.classList.toggle('is-sold-out', !available);

    item
      .querySelectorAll('[data-gb-po-minus], [data-gb-po-plus], [data-gb-po-qty-input]')
      .forEach((control) => {
        control.disabled = !available;
      });

    if (!available) {
      const input = item.querySelector('[data-gb-po-qty-input]');

      if (input) {
        input.value = '0';
      }

      item.classList.remove('is-selected');
    }
  }

  function removeInjectedAgoLabels(root) {
    if (!root) {
      return;
    }

    root.querySelectorAll('[data-gb-po-item]').forEach((item) => {
      const walker = document.createTreeWalker(item, NodeFilter.SHOW_TEXT);
      const textNodes = [];

      while (walker.nextNode()) {
        textNodes.push(walker.currentNode);
      }

      textNodes.forEach((textNode) => {
        if (!AGO_PATTERN.test(normalizeText(textNode.textContent))) {
          return;
        }

        const parent = textNode.parentElement;

        if (
          parent?.closest(
            '.gb-po__title, .gb-po__note, .gb-po__sku, .gb-po__price, .gb-po__compare, .gb-po__sold-out'
          )
        ) {
          return;
        }

        if (
          parent &&
          parent !== item &&
          AGO_PATTERN.test(normalizeText(parent.textContent))
        ) {
          parent.remove();
        } else {
          textNode.remove();
        }
      });
    });
  }

  function collectLineItemProperties(formData) {
    const properties = {};

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);

      if (match && value !== '') {
        properties[match[1]] = value;
      }
    }

    return properties;
  }

  function initializeModule(module, form) {
    if (!module || !form || module.dataset.gbInitialized === 'true') {
      return;
    }

    const submitButton = findSubmitButton(form);

    if (!submitButton) {
      return;
    }

    module.dataset.gbInitialized = 'true';

    const formatter = makeFormatter(module.dataset.currency || 'USD');
    const showCompareAt = module.dataset.showCompareAt === 'true';
    let basePrice = Number(module.dataset.basePrice || 0);
    let baseCompareAt = Number(module.dataset.baseCompareAt || 0);
    let lastVariantId = String(
      form.querySelector('[name="id"]')?.value ||
        module.dataset.initialVariantId ||
        ''
    );

    const defaultSubmitText = getButtonText(submitButton) || 'Add to cart';
    const items = Array.from(module.querySelectorAll('[data-gb-po-item]'));
    const setupTotalElement = module.querySelector('[data-gb-po-setup-total]');
    const setupCompareElement = module.querySelector('[data-gb-po-setup-compare]');
    const selectedLabelElement = module.querySelector(
      '[data-gb-po-selected-label]'
    );
    const messageElement = module.querySelector('[data-gb-po-message]');

    const getSelectedItems = () =>
      items.filter((item) => getItemQuantity(item) > 0);

    const setMessage = (message) => {
      if (messageElement) {
        messageElement.textContent = message || '';
      }
    };

    const updateSummary = () => {
      const selectedItems = getSelectedItems();
      const totalUnits = selectedItems.reduce(
        (sum, item) => sum + getItemQuantity(item),
        0
      );
      const baseQuantity = getBaseQuantity(form);
      const addOnTotal = selectedItems.reduce(
        (sum, item) =>
          sum + Number(item.dataset.price || 0) * getItemQuantity(item),
        0
      );
      const addOnCompareTotal = selectedItems.reduce((sum, item) => {
        const price = Number(item.dataset.price || 0);
        const compareAt = Number(item.dataset.compareAt || 0);
        const effectiveCompareAt = compareAt > price ? compareAt : price;

        return sum + effectiveCompareAt * getItemQuantity(item);
      }, 0);
      const effectiveBaseCompareAt =
        baseCompareAt > basePrice ? baseCompareAt : basePrice;
      const setupTotal = basePrice * baseQuantity + addOnTotal;
      const setupCompareTotal =
        effectiveBaseCompareAt * baseQuantity + addOnCompareTotal;
      const showSetupCompare =
        showCompareAt &&
        setupCompareTotal > setupTotal;

      items.forEach((item) => {
        item.classList.toggle('is-selected', getItemQuantity(item) > 0);
      });

      if (setupTotalElement) {
        setupTotalElement.textContent = formatter.format(setupTotal / 100);
      }

      if (setupCompareElement) {
        setupCompareElement.textContent = showSetupCompare
          ? formatter.format(setupCompareTotal / 100)
          : '';
        setupCompareElement.classList.toggle('is-hidden', !showSetupCompare);
      }

      if (selectedLabelElement) {
        if (!selectedItems.length) {
          selectedLabelElement.textContent = 'No add-ons selected';
        } else {
          const optionLabel =
            selectedItems.length === 1 ? 'add-on' : 'add-ons';
          const unitLabel = totalUnits === 1 ? 'unit' : 'units';

          selectedLabelElement.textContent =
            `${selectedItems.length} ${optionLabel} · ${totalUnits} ${unitLabel}`;
        }
      }

      setButtonText(
        submitButton,
        totalUnits > 0 ? 'Add setup to cart' : defaultSubmitText
      );

      removeInjectedAgoLabels(module);
      notifyDeliverySystem(module);
    };

    items.forEach((item) => {
      const variantSelect = item.querySelector('[data-gb-po-variant]');
      const quantityInput = item.querySelector('[data-gb-po-qty-input]');
      const minusButton = item.querySelector('[data-gb-po-minus]');
      const plusButton = item.querySelector('[data-gb-po-plus]');
      const priceElement = item.querySelector('[data-gb-po-price]');
      const compareElement = item.querySelector('[data-gb-po-compare]');

      const initialSelectedOption =
        variantSelect?.options?.[variantSelect.selectedIndex] || null;

      updateItemShippingData(
        item,
        initialSelectedOption
      );

      minusButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        setItemQuantity(item, getItemQuantity(item) - 1);
        setMessage('');
        updateSummary();
      });

      plusButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        setItemQuantity(item, getItemQuantity(item) + 1);
        setMessage('');
        updateSummary();
      });

      quantityInput?.addEventListener('input', () => {
        const quantity = clampInteger(quantityInput.value);
        item.classList.toggle('is-selected', quantity > 0);
        setMessage('');
        updateSummary();
      });

      quantityInput?.addEventListener('change', () => {
        setItemQuantity(item, quantityInput.value);
        setMessage('');
        updateSummary();
      });

      quantityInput?.addEventListener('blur', () => {
        setItemQuantity(item, quantityInput.value);
      });

      if (variantSelect) {
        let wasFocusedBeforePointerDown = false;

        variantSelect.addEventListener('pointerdown', () => {
          wasFocusedBeforePointerDown =
            document.activeElement === variantSelect;
        });

        /*
          Clicking an already-focused native select closes it. Blur immediately
          afterward so it returns to hover-only styling while the pointer remains
          over it, then to normal styling when the pointer leaves.
        */
        variantSelect.addEventListener('pointerup', () => {
          if (!wasFocusedBeforePointerDown) {
            return;
          }

          window.requestAnimationFrame(() => {
            variantSelect.blur();
          });
        });

        variantSelect.addEventListener('keyup', (event) => {
          if (event.key !== 'Enter' && event.key !== 'Escape') {
            return;
          }

          window.requestAnimationFrame(() => {
            variantSelect.blur();
          });
        });
      }

      variantSelect?.addEventListener('change', () => {
        const option = variantSelect.options[variantSelect.selectedIndex];

        if (!option) {
          return;
        }

        const available = option.dataset.available === 'true';

        item.dataset.variantId = option.value;
        item.dataset.price = option.dataset.price || '0';
        item.dataset.compareAt = option.dataset.compareAt || '0';

        updateItemShippingData(
          item,
          option
        );

        setItemAvailability(item, available);

        if (priceElement) {
          priceElement.textContent = option.dataset.priceFormatted || '';
        }

        if (compareElement) {
          const compareAt = Number(option.dataset.compareAt || 0);
          const price = Number(option.dataset.price || 0);
          const showCompareAt = compareAt > price;

          compareElement.textContent = showCompareAt
            ? option.dataset.compareAtFormatted || ''
            : '';

          compareElement.classList.toggle('is-hidden', !showCompareAt);
        }

        setMessage('');
        updateSummary();

        window.requestAnimationFrame(() => {
          variantSelect.blur();
        });
      });
    });

    const updateFromBaseQuantity = (event) => {
      if (event.target.matches('[name="quantity"]')) {
        updateSummary();
      }
    };

    form.addEventListener('input', updateFromBaseQuantity);
    form.addEventListener('change', updateFromBaseQuantity);

    form.addEventListener('click', (event) => {
      if (event.target.closest('button')) {
        window.requestAnimationFrame(updateSummary);
      }
    });

    const refreshBasePrice = async () => {
      const currentVariantId = String(
        form.querySelector('[name="id"]')?.value || ''
      );

      if (!currentVariantId || currentVariantId === lastVariantId) {
        return;
      }

      lastVariantId = currentVariantId;

      try {
        const response = await fetch(
          `${window.Shopify.routes.root}variants/${currentVariantId}.js`,
          {
            headers: {
              Accept: 'application/json'
            }
          }
        );

        if (!response.ok) {
          return;
        }

        const variant = await response.json();

        if (Number.isFinite(Number(variant.price))) {
          basePrice = Number(variant.price);
          baseCompareAt = Number(variant.compare_at_price || 0);
          updateSummary();
        }
      } catch (error) {
        console.warn(
          'Gymbuild purchase options: unable to refresh variant price.',
          error
        );
      }
    };

    form.addEventListener('change', refreshBasePrice);

    form.addEventListener(
      'submit',
      async (event) => {
        const selectedItems = getSelectedItems();

        if (!selectedItems.length) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const formData = new FormData(form);
        const baseVariantId = Number(formData.get('id'));

        if (!baseVariantId) {
          setMessage('The selected product variant could not be identified.');
          return;
        }

        const originalDisabled = submitButton.disabled;

        submitButton.disabled = true;
        setButtonText(submitButton, 'Adding setup...');

        try {
          const baseItem = {
            id: baseVariantId,
            quantity: getBaseQuantity(form)
          };

          const sellingPlan = formData.get('selling_plan');

          if (sellingPlan) {
            baseItem.selling_plan = Number(sellingPlan);
          }

          const properties = collectLineItemProperties(formData);

          if (Object.keys(properties).length) {
            baseItem.properties = properties;
          }

          const cartItems = [baseItem];

          selectedItems.forEach((item) => {
            const variantId = Number(item.dataset.variantId);
            const quantity = getItemQuantity(item);

            if (variantId && quantity > 0) {
              cartItems.push({
                id: variantId,
                quantity
              });
            }
          });

          const response = await fetch(
            `${window.Shopify.routes.root}cart/add.js`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: JSON.stringify({
                items: cartItems
              })
            }
          );

          const responseData = await response.json();

          if (!response.ok) {
            throw responseData;
          }

          setMessage('');
          window.location.href = `${window.Shopify.routes.root}cart`;
        } catch (error) {
          const message =
            error?.description ||
            error?.message ||
            'Unable to add the selected setup to the cart.';

          setMessage(message);
          submitButton.disabled = originalDisabled;
          setButtonText(
            submitButton,
            getSelectedItems().length
              ? 'Add setup to cart'
              : defaultSubmitText
          );
        }
      },
      true
    );

    if (module.dataset.hideDynamicCheckout === 'true') {
      const hideDynamicCheckout = () => {
        form
          .closest('.product-block--form, .product-form--regular, .product-details')
          ?.querySelectorAll(
            '.shopify-payment-button, [data-shopify="payment-button"]'
          )
          .forEach((element) => {
            element.hidden = true;
            element.setAttribute('aria-hidden', 'true');
            element.style.display = 'none';
          });
      };

      hideDynamicCheckout();

      [150, 600, 1500].forEach((delay) => {
        window.setTimeout(hideDynamicCheckout, delay);
      });
    }

    removeInjectedAgoLabels(module);
    updateSummary();
  }

  function mountSource(source) {
    if (!source) {
      return false;
    }

    const instanceId = source.dataset.gbPoSourceId;
    let module = source.querySelector('gb-purchase-options');

    if (module && !source._gbPurchaseOptionsHTML) {
      source._gbPurchaseOptionsHTML = module.outerHTML;
    }

    if (!module && instanceId) {
      module = document.querySelector(
        `[data-gb-po-instance="${CSS.escape(instanceId)}"]`
      );
    }

    if (!module && source._gbPurchaseOptionsHTML) {
      const holder = document.createElement('div');
      holder.innerHTML = source._gbPurchaseOptionsHTML;
      module = holder.firstElementChild;
    }

    if (!module) {
      return false;
    }

    const form = findProductForm();

    if (!form) {
      return false;
    }

    const submitButton = findSubmitButton(form);

    if (!submitButton) {
      return false;
    }

    const atcContainer =
      submitButton.closest('.product-form--atc') ||
      submitButton.parentElement;

    const actionWrapper =
      submitButton.closest('.product-form__action-wrapper') ||
      submitButton;

    if (!atcContainer || !actionWrapper) {
      return false;
    }

    atcContainer.classList.add('gb-po-atc-layout');

    if (!atcContainer.contains(module)) {
      atcContainer.insertBefore(module, actionWrapper);
    }

    if (actionWrapper.parentElement === atcContainer) {
      atcContainer.appendChild(actionWrapper);
    }

    source
      .closest('.shopify-section')
      ?.classList.add('gb-po-section--mounted');

    initializeModule(module, form);
    removeInjectedAgoLabels(module);
    source.dataset.gbPoMounted = 'true';

    return true;
  }

  let mountObserver = null;
  let mountFramePending = false;
  let mountStopTimer = null;

  function mountAll() {
    const sources = Array.from(document.querySelectorAll(SOURCE_SELECTOR));

    if (!sources.length) {
      return false;
    }

    let allMounted = true;

    sources.forEach((source) => {
      const mounted =
        source.dataset.gbPoMounted === 'true' || mountSource(source);

      if (!mounted) {
        allMounted = false;
      }
    });

    if (allMounted) {
      stopMountObserver();
    }

    return allMounted;
  }

  function scheduleMount() {
    if (mountFramePending) {
      return;
    }

    mountFramePending = true;

    window.requestAnimationFrame(() => {
      mountFramePending = false;
      mountAll();
    });
  }

  function stopMountObserver() {
    if (mountObserver) {
      mountObserver.disconnect();
      mountObserver = null;
    }

    if (mountStopTimer) {
      window.clearTimeout(mountStopTimer);
      mountStopTimer = null;
    }
  }

  function startMountWindow() {
    stopMountObserver();

    if (mountAll()) {
      return;
    }

    mountObserver = new MutationObserver(scheduleMount);

    mountObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    [80, 200, 500, 1000, 1800].forEach((delay) => {
      window.setTimeout(scheduleMount, delay);
    });

    mountStopTimer = window.setTimeout(() => {
      stopMountObserver();
      mountAll();
    }, 3000);
  }

  function start() {
    startMountWindow();

    document.addEventListener('shopify:section:load', startMountWindow);
    window.addEventListener('pageshow', scheduleMount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, {
      once: true
    });
  } else {
    start();
  }
})();
